import React from "react";
import styled from "styled-components";


const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.4);
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ModalContainer = styled.div`
  background: #fff;
  border-radius: 8px;
  max-width: 500px;
  max-height: 90vh;
  width: 90%;
  padding: 20px;
  position: relative;
  box-shadow: 0 2px 10px rgba(0,0,0,0.3);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  .swal-icon {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
    box-sizing: border-box;
  }

  .swal-icon svg {
    width: 40px;
    height: 40px;
  }

  .swal-icon--success {
    border: 4px solid #53a587;
    color: #53a587;
  }

  .swal-icon--error {
    border: 4px solid #e15151;
    color: #e15151;
  }

  /* Warning Icon Styles */
  .swal-icon--warning {
    width: 80px;
    height: 80px;
    border: 4px solid #f8bb86;
    border-radius: 50%;
    padding: 0;
    position: relative;
    box-sizing: content-box;
    margin: 0 auto 20px;
  }

  .swal-icon--warning__body {
    position: absolute;
    width: 5px;
    height: 47px;
    left: 50%;
    top: 10%;
    background-color: #f8bb86;
    transform: translateX(-50%);
  }

  .swal-icon--warning__dot {
    position: absolute;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    left: 50%;
    bottom: -6px;
    background-color: #f8bb86;
    transform: translateX(-50%);
  }

  .swal-title {
    font-size: 24px;
    margin-bottom: 10px;
    text-align: center;
    color: rgba(0, 0, 0, .65);
    font-weight: bold;
  }

  .swal-text {
    font-size: 16px;
    margin-bottom: 20px;
    text-align: center;
    color: #545454;
    white-space: pre-wrap;
    word-break: break-word;
    overflow-y: auto;
    overflow-x: hidden;
    max-height: 50vh;
    padding-right: 4px;
  }

  .swal-body {
    margin-bottom: 20px;
  }
`;


const CloseX = styled.button`
  position: absolute;
  top: 8px;
  right: 12px;
  background: transparent;
  border: none;
  font-size: 20px;
  cursor: pointer;
`;

const Footer = styled.div`
  display: flex;
  justify-content: ${({ $single }) => ($single ? "center" : "flex-end")};
  gap: 10px;
`;

const CancelButton = styled.button`
  background: transparent;
  color: #53a587;
  border: 2px solid #53a587;
  border-radius: 4px;
  font-size: 16px;
  padding: 8px 20px;
  cursor: pointer;

  &:hover {
    background-color: rgba(83,165,135, 0.1);
  }
`;

const ConfirmButton = styled.button`
  background: #53a587;
  color: #fff;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  padding: 8px 20px;
  cursor: pointer;

  &:hover:not(:disabled) {
    background-color: #469e78;
  }

  &:disabled {
    background-color: #a7c5b9;
    cursor: not-allowed;
  }
`;


const ConfirmationModal = ({ 
  show, 
  title = "Abort Ingestion", 
  message = "Are you sure you would like to abort the ingestion process?", 
  onCancel = () => {}, 
  onConfirm = () => {}, 
  status,
  single = false,
  confirmLabel,
  cancelLabel,
  children,
  confirmDisabled = false,
}) => {
  if (!show) return null;

  const normalizedStatus = typeof status === "string"
    ? status.toLowerCase()
    : "";
  const iconType =
    normalizedStatus === "success"
      ? "success"
      : normalizedStatus === "failed"
      ? "error"
      : "warning";

  const primaryLabel = confirmLabel || (single ? "Close" : "OK");
  const secondaryLabel = cancelLabel || "Cancel";

  const renderIcon = () => {
    if (iconType === "success") {
      return (
        <div className="swal-icon swal-icon--success" aria-hidden="true">
          <svg viewBox="0 0 24 24" focusable="false">
            <path
              fill="currentColor"
              d="M9.00016 16.2L4.80016 12L3.40016 13.4L9.00016 19L21.0002 7.00002L19.6002 5.60002L9.00016 16.2Z"
            />
          </svg>
        </div>
      );
    }

    if (iconType === "error") {
      return (
        <div className="swal-icon swal-icon--error" aria-hidden="true">
          <svg viewBox="0 0 24 24" focusable="false">
            <path
              fill="currentColor"
              d="M12 2C6.47998 2 2 6.47998 2 12C2 17.52 6.47998 22 12 22C17.52 22 22 17.52 22 12C22 6.47998 17.52 2 12 2ZM15.59 16.59L12 13L8.40997 16.59L7 15.18L10.59 11.59L7 8L8.40997 6.59003L12 10.18L15.59 6.59003L17 8L13.41 11.59L17 15.18L15.59 16.59Z"
            />
          </svg>
        </div>
      );
    }

    return (
      <div className="swal-icon swal-icon--warning" aria-hidden="true">
        <span className="swal-icon--warning__body">
          <span className="swal-icon--warning__dot"></span>
        </span>
      </div>
    );
  };

  return (
    <ModalOverlay>
      <ModalContainer aria-modal="true">
        <CloseX onClick={onCancel} aria-label="Close">×</CloseX>

        {renderIcon()}

        <div className="swal-title">{title}</div>
        {message !== undefined && message !== null && (
          <div className="swal-text">{message}</div>
        )}
        {children ? <div className="swal-body">{children}</div> : null}

        <Footer $single={single}>
          {!single && (
            <CancelButton onClick={onCancel}>{secondaryLabel}</CancelButton>
          )}
          <ConfirmButton onClick={onConfirm} disabled={confirmDisabled}>
            {primaryLabel}
          </ConfirmButton>
        </Footer>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default ConfirmationModal;
